import * as config from "../config.js";
import { metadataText, parseMetadataText } from "../utils/datasetMetadata";

export const getDataSets = async (clientID) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/client/${clientID}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("data SET ERROR: ", error);
  });

  const results = await response.json();
  return results;
};

export const getDataSet = async (dataSetID) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/${dataSetID}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("data SET ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const saveDataSet = async (dataSetData, clientID) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/${dataSetData.public_id ? dataSetData.public_id : ""}`;
  //dataSetData.ingestion_workflow = JSON.stringify(dataSetData.ingestion_workflow);
  const response = await fetch(url, {
    method: dataSetData.public_id ? "PUT" : "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({
      ...dataSetData,
      client_id: clientID,
    }),
  }).catch((error) => {
    console.log("SAVE data SET ERROR: ", error);
  });

  const results = await response.json();
  return results;
};

/**
 * Update just a dataset's item metadata schema.
 *
 * Used when a dataset type's schema changes and its datasets must pick up the new fields.
 */
export const saveDataSetSchema = async (publicId, metadataSchema) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/${publicId}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({ metadata_schema: metadataSchema }),
  }).catch((error) => {
    console.log("SAVE data SET SCHEMA ERROR: ", error);
  });

  return readJsonResponse(response);
};

export const queryDataSet = (dataSetID, query, queryType, docCount) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/query/${dataSetID}`;
  return fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({
      query,
      query_type: queryType,
      doc_count: docCount,
    }),
  })
    .then((response) => response.json())
    .catch((error) => {
      console.log("QUERY data SET ERROR: ", error);
    });
}

export const deleteDataSet = async (dataSetID) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/${dataSetID}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("DELETE data SET ERROR: ", error);
  });

  const results = await response.json();
  return results;
};

export const runIngestionWorkflow = async (dataSetID, document = "This is a test document for troubleshooting the workflow") => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/ingest/${dataSetID}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({
      document,
    }),
  }).catch((error) => {
    console.log("INGESTION WORKFLOW ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const saveDataItem = async (clientId, dataItem) => {
  const url = `${config.REST_SERVER_URL}/admin/dataitem/${clientId}`;

  let bodyData = JSON.stringify({
    ...dataItem,
  });

  const headers = {};

  if (dataItem.sourceType === "file-upload" && dataItem.file) {
    const fd = new FormData();
    fd.append("file", dataItem.file);
    fd.append("data_item", JSON.stringify({
      ...dataItem,
      file: undefined, // We remove the file from the JSON data since it's being sent as form data
    }));
    bodyData = fd;
  } else {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    method: dataItem.public_id ? "PUT" : "POST",
    crossDomain: true,
    headers: {
      ...headers,
    },
    credentials: "include",
    body: bodyData
  }).catch((error) => {
    console.log("SAVE data ITEM ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const saveDataSetItem = async (dataSetID, mediaID, supportingContentID, metadata) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/additem/${dataSetID}`;

  const body = {
    media_id: mediaID,
    supporting_content_id: supportingContentID,
  };

  if (metadata !== undefined) {
    const parsed = parseMetadataText(typeof metadata === "string" ? metadata : metadataText(metadata));
    if (!parsed.ok) return { success: false, message: parsed.message };
    body.metadata = parsed.value;
  }

  const response = await fetch(url, {
    method: "POST",
    crossDomain: true,
    headers: {
      Accept: "application/json",
    }, credentials: "include",
    body: JSON.stringify(body),
  }).catch((error) => {
    console.log("SAVE data ITEM TO data SET ERROR: ", error);
  });

  return readJsonResponse(response);
}

// Reads a JSON body without exploding on empty or non-JSON responses (e.g. an HTML error
// page from a proxy), which would otherwise surface as "cannot read properties of undefined".
const readJsonResponse = async (response) => {
  if (!response) {
    return { success: false, message: "No response from the server." };
  }

  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    return { success: false, message: `Could not read the response: ${error?.message}` };
  }

  let results;
  try {
    results = text ? JSON.parse(text) : {};
  } catch (error) {
    return {
      success: false,
      message: `Unexpected non-JSON response (HTTP ${response.status}): ${String(text).slice(0, 200)}`,
    };
  }

  if (response.ok === false) {
    return {
      ...results,
      success: false,
      message: results?.message || `Request failed with HTTP ${response.status}`,
    };
  }

  return results;
};

export const saveDataSetItems = async (dataSetID, mediaIDs, metadataByMediaId = null) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/additems/${dataSetID}`;

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      }, credentials: "include",
      body: JSON.stringify({
        media_ids: mediaIDs,
        ...(metadataByMediaId && Object.keys(metadataByMediaId).length > 0
          ? { metadata: metadataByMediaId }
          : {}),
      }),
    });
  } catch (error) {
    console.log("SAVE data ITEM TO data SET ERROR: ", error);
    return { success: false, message: error?.message || "Request failed." };
  }

  return readJsonResponse(response);
}

// Files per request. The bulk additems endpoint only answers once a request is finished, so
// this is what progress is measured in: it reports after each batch. At 1 every file is its
// own request, so the counts advance per file (and one bad file doesn't fail its neighbours),
// at the cost of one round trip per file. Raise it to trade update frequency for fewer calls.
const SAVE_DATA_ITEMS_BATCH_SIZE = 1;

const describeBatchSize = () =>
  SAVE_DATA_ITEMS_BATCH_SIZE === 1
    ? "one file at a time"
    : `in batches of ${SAVE_DATA_ITEMS_BATCH_SIZE}`;

/**
 * Add files to a data set, reporting progress after every batch.
 *
 * Uses the bulk `additems` endpoint (the one the bulk save has always used) rather than
 * `additem`, so behaviour matches a single save. Logs a line per batch, e.g.
 * `30 of 147 saved, 117 of 147 remaining`, and returns an aggregate result so a failed
 * batch doesn't discard the files that did save.
 *
 * @param {string} dataSetID
 * @param {string[]} mediaIDs
 * @param {(progress: {total: number, saved: number, remaining: number, failed: number}) => void} [onProgress]
 *   Optional callback for driving UI; the console log happens regardless.
 */
export const saveDataSetItemsWithProgress = async (dataSetID, mediaIDs = [], onProgress = null, metadataByMediaId = null) => {
  const total = (mediaIDs || []).length;
  const items = [];
  const failed = [];
  let saved = 0;

  const report = (batch) => {
    const progress = { total, saved, remaining: total - saved, failed: failed.length };
    console.log(
      `!!! DATA SET SAVE PROGRESS (${dataSetID}): ${saved} of ${total} saved, ${progress.remaining} of ${total} remaining`
    );
    if (onProgress) {
      onProgress({ ...progress, batch });
    }
    return progress;
  };

  if (total === 0) {
    return { success: false, items, failed, saved: 0, total: 0, message: "No data items selected." };
  }

  console.log(
    `!!! SAVING ${total} DATA ITEM(S) TO DATA SET ${dataSetID} ${describeBatchSize()}`
  );

  for (let start = 0; start < total; start += SAVE_DATA_ITEMS_BATCH_SIZE) {
    const batch = mediaIDs.slice(start, start + SAVE_DATA_ITEMS_BATCH_SIZE);

    const results = await saveDataSetItems(dataSetID, batch, metadataByMediaId);

    if (results?.success) {
      saved += batch.length;
      const batchItems = results.items || (results.item ? [results.item] : []);
      items.push(...batchItems);
    } else {
      failed.push(...batch);
      console.log(
        `!!! DATA SET SAVE FAILED (${dataSetID}) for ${batch.length} item(s): `,
        results?.message || results,
        { sampleIds: batch.slice(0, 3) }
      );
    }

    report(batch);
  }

  const result = {
    success: saved > 0,
    items,
    failed,
    saved,
    total,
    message: failed.length > 0
      ? `Saved ${saved} of ${total} data item(s); ${failed.length} failed to save.`
      : "Data items saved successfully.",
  };

  console.log(
    `!!! DATA SET SAVE COMPLETE (${dataSetID}): ${saved} of ${total} saved, ${failed.length} failed`
  );

  return result;
}

export const getDataSetItems = async (dataSetID) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/items/${dataSetID}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("data SET ITEMS ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const deleteDataSetItem = async (dataSetId, mediaId) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/removeitem/${dataSetId}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({
      media_id: mediaId,
    }),
  }).catch((error) => {
    console.log("REMOVE data ITEM FROM data SET ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const ingestDataSetItem = async (dataSetId, mediaId, metadata) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/ingest/${dataSetId}`;

  const body = { media_id: mediaId };

  // Omit `metadata` entirely to inherit the item's existing metadata.
  if (metadata !== undefined) {
    const parsed = parseMetadataText(typeof metadata === "string" ? metadata : metadataText(metadata));
    if (!parsed.ok) return { success: false, message: parsed.message };
    body.metadata = parsed.value;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify(body),
  }).catch((error) => {
    console.log("INGEST data ITEM TO data SET ERROR: ", error);
  });

  // 400s carry `errors[]` with a path and message per problem, so keep them.
  return readJsonResponse(response);
}

/**
 * Save a data item's metadata.
 *
 * Uses the dataset-item endpoint (`PUT /admin/dataset/item/<data_set_item_public_id>`), which is
 * validated against the dataset's `metadata_schema` server-side. Validation failures come back as
 * `errors[]` with a `path` and `message` per problem.
 *
 * @param {string} itemPublicId The **dataset item** public id.
 * @param {string|object|null} metadata JSON text, a value, or null to clear it.
 * @returns {Promise<{success: boolean, message?: string, errors?: object[], item?: object}>}
 */
export const saveItemMetadata = async (itemPublicId, metadata) => {
  const parsed = parseMetadataText(typeof metadata === "string" ? metadata : metadataText(metadata));

  if (!parsed.ok) {
    return { success: false, message: parsed.message };
  }

  if (!itemPublicId) {
    return { success: false, message: "This item has no id to save against." };
  }

  const url = `${config.REST_SERVER_URL}/admin/dataset/item/${itemPublicId}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
    body: JSON.stringify({ metadata: parsed.value }),
  }).catch((error) => {
    console.log("SAVE data ITEM METADATA ERROR: ", error);
  });

  return readJsonResponse(response);
};

export const getDataSetItem = async (dataSetId, mediaId) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/dataitem/bymedia/${dataSetId}/${mediaId}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("data ITEM ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

export const getDataSetItemChunks = async (dataSetId, mediaId) => {
  const url = `${config.REST_SERVER_URL}/admin/dataset/dataitem/chunks/bymedia/${dataSetId}/${mediaId}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    }, credentials: "include",
  }).catch((error) => {
    console.log("data ITEM ERROR: ", error);
  });

  const results = await response.json();
  return results;
}

